/**
 * Inbox utility functions
 */

// Format date/time for email list
export const formatEmailTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  
  if (isToday) {
    return date.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' });
};

// Get category color class
export const getCategoryColor = (category) => {
  const colors = {
    'Kontrakt': 'bg-violet-100 text-violet-700 border-violet-200',
    'Arbeidsrett': 'bg-purple-100 text-purple-700 border-purple-200',
    'Forvaltning': 'bg-amber-100 text-amber-700 border-amber-200',
    'Eiendom': 'bg-green-100 text-green-700 border-green-200',
    'Selskapsrett': 'bg-indigo-100 text-indigo-700 border-indigo-200',
    'Tvist': 'bg-red-100 text-red-700 border-red-200',
    'Familie': 'bg-pink-100 text-pink-700 border-pink-200',
    'Arv': 'bg-orange-100 text-orange-700 border-orange-200',
    'default': 'bg-gray-100 text-gray-700 border-gray-200'
  };
  return colors[category] || colors.default;
};

// Get risk level styling
export const getRiskColor = (riskLevel) => {
  switch (riskLevel?.toUpperCase()) {
    case 'HIGH':
      return 'bg-red-100 text-red-700 border-red-200';
    case 'MEDIUM':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'LOW':
      return 'bg-green-100 text-green-700 border-green-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};

// Get priority icon color
export const getPriorityColor = (priority) => {
  switch (priority?.toUpperCase()) {
    case 'URGENT':
      return 'text-red-500';
    case 'HIGH':
      return 'text-amber-500';
    case 'MEDIUM':
      return 'text-violet-500';
    default:
      return 'text-gray-400';
  }
};

// Filter emails based on filter type
export const filterEmails = (emails, filter, searchQuery) => {
  let filtered = emails;
  
  // Apply filter
  switch (filter) {
    case 'unread':
      filtered = filtered.filter(e => !e.read_by?.length && !e.is_read);
      break;
    case 'unassigned':
      filtered = filtered.filter(e => !e.case_id);
      break;
    case 'assigned':
      filtered = filtered.filter(e => e.case_id);
      break;
    case 'urgent':
      filtered = filtered.filter(e => e.ai_analysis?.priority === 'URGENT' || e.ai_analysis?.priority === 'HIGH');
      break;
    case 'high_risk':
      filtered = filtered.filter(e => e.ai_analysis?.risk_level === 'HIGH');
      break;
    default:
      break;
  }
  
  // Apply search
  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    filtered = filtered.filter(e => 
      e.subject?.toLowerCase().includes(query) ||
      e.from?.name?.toLowerCase().includes(query) ||
      e.from?.email?.toLowerCase().includes(query) ||
      e.body_preview?.toLowerCase().includes(query)
    );
  }
  
  return filtered;
};

// Group emails into threads
export const groupEmailsIntoThreads = (emails, threads) => {
  if (!threads || threads.length === 0) {
    return emails.map(e => ({
      id: e.id,
      conversation_id: e.outlook_conversation_id || e.id,
      message_count: 1,
      messages: [e],
      latest_message: e,
      has_attachments: e.has_attachments,
      preview: e.body_preview || e.body?.substring(0, 100)
    }));
  }
  return threads;
};

// Calculate inbox stats
export const calculateInboxStats = (emails) => {
  // "needsTriage" = emails without case_id that need action
  // This matches the backend logic: no case_id AND (pending or no triage_status)
  const needsTriage = emails.filter(e => {
    const hasNoCase = !e.case_id;
    const isPendingOrNew = !e.triage_status || e.triage_status === 'PENDING';
    return hasNoCase && isPendingOrNew;
  }).length;
  
  return {
    total: emails.length,
    unread: emails.filter(e => !e.read_by?.length && !e.is_read).length,
    unassigned: needsTriage, // Now correctly shows emails needing triage (not assigned)
    urgent: emails.filter(e => e.ai_analysis?.priority === 'URGENT' || e.ai_analysis?.priority === 'HIGH').length,
    highRisk: emails.filter(e => e.ai_analysis?.risk_level === 'HIGH').length,
    assignedToCases: emails.filter(e => !!e.case_id).length // Emails linked to cases
  };
};

// Storage helpers
export const STORAGE_KEYS = {
  PANEL_WIDTH: 'lexy_inbox_panel_width',
  AI_DRAWER_WIDTH: 'lexy_inbox_ai_drawer_width',
  AI_DRAWER_PINNED: 'lexy_inbox_ai_drawer_pinned'
};

export const loadFromStorage = (key, defaultValue) => {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : defaultValue;
  } catch {
    return defaultValue;
  }
};

export const saveToStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    console.warn('Failed to save to localStorage:', key);
  }
};
